/** Layout primitives: Card, Badge, Table, Tabs (Radix), Dialog (Radix). */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import {
  type ComponentProps,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/format";

/** Surface container. */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      className={cn("min-w-0 rounded-md border bg-card text-card-foreground", className)}
      {...props}
    />
  );
}

/** Card header row. */
export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      className={cn("flex flex-col space-y-1 border-b bg-muted/50 px-3 py-2", className)}
      {...props}
    />
  );
}

/** Card title. */
export function CardTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>): ReactElement {
  return <h3 className={cn("text-xs font-semibold tracking-tight", className)} {...props} />;
}

/** Card body. */
export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return <div className={cn("p-3", className)} {...props} />;
}

/** Badge variants. */
export const badgeVariants = cva(
  "inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold leading-tight whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral:
          "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-200",
        info: "border-[#92d5d8] bg-[#e6f7f6] text-[#0f5450] dark:border-[#2d6a6e] dark:bg-[#133840] dark:text-[#92d5d8]",
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/70 dark:bg-emerald-950/60 dark:text-emerald-300",
        warning:
          "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/70 dark:bg-amber-950/60 dark:text-amber-300",
        danger:
          "border-red-200 bg-red-50 text-red-800 dark:border-red-800/70 dark:bg-red-950/60 dark:text-red-300",
        outline: "border-border text-foreground",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

/** Small status pill. */
export function Badge({
  className,
  tone,
  ...props
}: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>): ReactElement {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

/** Table wrapper with horizontal scroll. */
export function Table({ className, ...props }: ComponentProps<"table">): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="min-w-0 max-w-full overflow-auto rounded-md border bg-card"
      role="region"
      aria-label={t("app.table")}
      tabIndex={0}
    >
      <table className={cn("w-full caption-bottom text-xs", className)} {...props} />
    </div>
  );
}

/** Table header cell. */
export function Th({ className, ...props }: ComponentProps<"th">): ReactElement {
  return (
    <th
      className={cn(
        "h-8 bg-muted px-2.5 text-start align-middle text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Table data cell. */
export function Td({ className, ...props }: ComponentProps<"td">): ReactElement {
  return <td className={cn("px-2.5 py-1.5 align-middle text-xs", className)} {...props} />;
}

/** Table row with hover state. */
export function Tr({ className, ...props }: ComponentProps<"tr">): ReactElement {
  return (
    <tr
      className={cn("border-b transition-colors last:border-0 hover:bg-accent/70", className)}
      {...props}
    />
  );
}

/** Tabs root. */
export const Tabs = TabsPrimitive.Root;

/** Tabs list. */
export function TabsList({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>): ReactElement {
  return (
    <TabsPrimitive.List
      className={cn(
        "inline-flex min-h-9 max-w-full items-center justify-start overflow-x-auto rounded-md border bg-muted p-0.5 text-muted-foreground [@media(pointer:coarse)]:min-h-11",
        className,
      )}
      {...props}
    />
  );
}

/** Tabs trigger. */
export function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>): ReactElement {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-sm px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=active]:bg-card data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-xs",
        className,
      )}
      {...props}
    />
  );
}

/** Tabs content. */
export function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>): ReactElement {
  return <TabsPrimitive.Content className={cn("mt-2.5", className)} {...props} />;
}

/** Props for `Dialog`. */
export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Use a wider panel for editors. */
  wide?: boolean;
}

/** Modal dialog with title, body and optional footer. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: DialogProps): ReactElement {
  const { t } = useTranslation();
  const content = useRef<HTMLDivElement>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          ref={content}
          onOpenAutoFocus={(event) => {
            if (window.matchMedia("(pointer: coarse)").matches) {
              event.preventDefault();
              content.current?.focus();
            }
          }}
          className={cn(
            "fixed start-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain rounded-lg border bg-background p-4 shadow-lg [overflow-wrap:anywhere] sm:p-6 rtl:translate-x-1/2",
            wide ? "max-w-3xl" : "max-w-lg",
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-lg font-semibold">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">
                  {title}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="-me-2 -mt-2 flex size-11 shrink-0 items-center justify-center rounded-sm opacity-70 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={t("app.close")}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="space-y-4">{children}</div>
          {footer ? <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
